import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from './config/api';
import Spinner from 'react-bootstrap/Spinner';
import { FaArrowLeft, FaSearch, FaCheckCircle, FaCog } from 'react-icons/fa';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function EquipmentCard({ eq, isLinked }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '10px',
        border: isLinked ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
        padding: '0.85rem 1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
      }}
    >
      {eq.photoUrl ? (
        <img
          src={eq.photoUrl}
          alt=""
          style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', flexShrink: 0, background: '#f3f4f6' }}
          onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
        />
      ) : null}
      <div
        style={{
          width: 48, height: 48, borderRadius: 10, background: '#f3f4f6',
          display: eq.photoUrl ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <FaCog style={{ fontSize: '1.25rem', color: '#9ca3af' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.93rem' }}>{eq.name}</div>
        <div style={{ fontSize: '0.8rem', color: '#6b7280', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.1rem' }}>
          {eq.category && <span>{eq.category}</span>}
          {eq.location && <span>📍 {eq.location}</span>}
        </div>
      </div>
      {isLinked && (
        <FaCheckCircle style={{ color: '#10b981', fontSize: '1.1rem', flexShrink: 0 }} />
      )}
    </div>
  );
}

export default function MechVisitDetail() {
  const { buildingId, visitId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      try {
        const r = await fetch(`${API_BASE_URL}/api/mechanical/tech/building/${encodeURIComponent(buildingId)}/detail`);
        if (!r.ok) throw new Error(`Failed to load: ${r.status}`);
        const json = await r.json();
        if (!cancelled) {
          setData(json);
          setError(null);
        }
      } catch (e) {
        console.error('[MechVisitDetail] Error:', e);
        if (!cancelled) setError('Failed to load visit details.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [buildingId]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4rem 1rem' }}>
        <Spinner animation="border" style={{ color: '#2563eb', marginBottom: '1rem' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p style={{ color: '#6b7280', fontWeight: 500, margin: 0 }}>Loading visit...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: '2rem' }}>
        <button onClick={() => navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', fontWeight: 500, fontSize: '0.9rem', padding: 0, marginBottom: '1rem' }}>
          <FaArrowLeft /> Back to building
        </button>
        <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', color: '#991b1b' }}>{error || 'Visit not found.'}</div>
      </div>
    );
  }

  const { building, equipment = [], visits = [] } = data;
  const visit = visits.find((v) => v.id === visitId);

  if (!visit) {
    return (
      <div style={{ padding: '2rem' }}>
        <button onClick={() => navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', fontWeight: 500, fontSize: '0.9rem', padding: 0, marginBottom: '1rem' }}>
          <FaArrowLeft /> Back to building
        </button>
        <div style={{ padding: '1rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.5rem', color: '#92400e' }}>Visit not found. It may have been deleted.</div>
      </div>
    );
  }

  const linkedSet = new Set(visit.equipmentIds || []);

  const linkedEquipment = equipment.filter((eq) => linkedSet.has(eq.id));
  const otherEquipment = equipment.filter((eq) => !linkedSet.has(eq.id));
  const allSorted = [...linkedEquipment, ...otherEquipment];

  const filtered = allSorted.filter((eq) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (eq.name || '').toLowerCase().includes(q) ||
      (eq.category || '').toLowerCase().includes(q) ||
      (eq.location || '').toLowerCase().includes(q)
    );
  });

  return (
    <div style={{ padding: '1.5rem 2rem', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Back nav */}
        <button
          onClick={() => navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}`)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', fontWeight: 500, fontSize: '0.9rem', padding: 0, marginBottom: '1.25rem' }}
        >
          <FaArrowLeft /> Back to {building?.name || 'building'}
        </button>

        {/* Visit header */}
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>
          <h1 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#111827', margin: 0, marginBottom: '0.5rem' }}>
            {visit.name}
          </h1>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#6b7280' }}>
            <span>📅 {formatDate(visit.dateVisited)}</span>
            {visit.technician && <span>👤 {visit.technician}</span>}
            <span>⚙️ {linkedEquipment.length} equipment serviced</span>
          </div>
          {visit.details && (
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.9rem', color: '#374151', lineHeight: 1.5 }}>
              {visit.details}
            </p>
          )}
          {visit.photoUrls?.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
              {visit.photoUrls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={`Visit attachment ${i + 1}`} style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Equipment list */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', margin: 0 }}>
            Building Equipment ({equipment.length})
          </h2>
          <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 600 }}>
            {linkedEquipment.length} serviced in this visit
          </span>
        </div>

        {equipment.length > 4 && (
          <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
            <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.8rem' }} />
            <input
              type="text"
              placeholder="Search equipment..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', padding: '0.6rem 0.6rem 0.6rem 2.25rem', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.85rem', outline: 'none' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
              {search ? 'No equipment matches your search.' : 'No equipment found for this building.'}
            </div>
          ) : (
            filtered.map((eq) => (
              <EquipmentCard key={eq.id} eq={eq} isLinked={linkedSet.has(eq.id)} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
